import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { HOME, IMAGES } from "../mock";
import FIELD_NOTES from "../content/fieldNotes/generated";
import { Reveal, SectionHeading, Eyebrow, Ornament } from "../components/bits";
import VideoBackground from "../components/VideoBackground";
import MonographModal from "../components/MonographModal";
import { Dna, Sun, Gem, ArrowRight } from "lucide-react";

// Match the exact symbols used on "The Origins of NLFH" page.
const pillarIcon = {
  "Human Presence": Dna,
  "Heart Coherence": Sun,
  "Nonlocal Field": Gem,
};

const Para = ({ items }) =>
  items.map((p, i) => <p key={i}>{p}</p>);

const Home = () => {
  const { hero, paradigm, foundation, beyond, concept, pillars, closing } = HOME;
  const latestNote = FIELD_NOTES.filter((n) => n.lang === "en").reduce((a, n) => (!a || n.issue > a.issue ? n : a), null);
  const heroBgRef = useRef(null);
  const heroContentRef = useRef(null);

  // Gentle parallax: the Earth drifts slower than the page; text eases up and fades.
  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        if (heroBgRef.current) {
          heroBgRef.current.style.transform = `translate3d(0, ${y * 0.35}px, 0) scale(1.12)`;
        }
        if (heroContentRef.current) {
          heroContentRef.current.style.transform = `translate3d(0, ${y * 0.18}px, 0)`;
          heroContentRef.current.style.opacity = String(Math.max(0, 1 - y / 620));
        }
        raf = null;
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <main className="video-page">
      <VideoBackground veil={0.42} />
      {/* HERO */}
      <section className="hero hero-earth">
        <div className="hero-earth-bg" ref={heroBgRef}>
          <img src={IMAGES.earth} alt="Earth seen from space" loading="eager" />
        </div>
        <div className="hero-earth-overlay" />
        <div className="container-page hero-content" ref={heroContentRef}>
          <Reveal>
            <h1 className="hero-title">{hero.title}</h1>
          </Reveal>
          <Reveal delay={120}>
            <p className="hero-sub">
              {hero.sub1}
              <br />
              {hero.sub2}
            </p>
          </Reveal>
          <Reveal delay={220}>
            <p className="hero-lead">{hero.lead}</p>
          </Reveal>
          <Reveal delay={320}>
            <div className="hero-cta-wrap">
              <MonographModal>
                <button type="button" className="btn-gold">
                  Learn More about NLFH
                </button>
              </MonographModal>
              <p className="hero-cta-subtext">Download the Master Monograph (Free)</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* SUNDAY COMMON SESSION — free weekly invitation */}
      <section className="section-tight" style={{ background: "var(--paper-warm)" }}>
        <div className="container-page">
          <Reveal className="container-narrow" style={{ textAlign: "center" }}>
            <span className="eyebrow" style={{ color: "var(--sky)" }}>
              Every Sunday · 09:00 AM (CEST) · Free
            </span>
            <h2 className="h-section" style={{ marginTop: "0.6rem" }}>A Weekly Sunday Session</h2>
            <p className="lead" style={{ marginTop: "1rem" }}>
              A quiet shared space to rest in presence, coherence and nonlocal awareness.
              No meditation experience is required. Nothing to do. Simply rest.
            </p>
            <Link to="/contacts" className="btn-gold" style={{ marginTop: "1.75rem" }}>
              Enter the Sunday Field
            </Link>
          </Reveal>
        </div>
      </section>

      {/* LATEST FIELD NOTE — teaser */}
      {latestNote && (
        <section className="section-tight">
          <div className="container-page">
            <Reveal className="fn-teaser soft-card" as={Link} to={`/field-notes/${latestNote.slug}`} data-testid="home-field-note-teaser">
              <div className="fn-teaser-side">
                <span className="eyebrow" style={{ color: "var(--gold-strong)" }}>From the Field Notes</span>
                <span className="fn-teaser-cat">{latestNote.category}</span>
              </div>
              <div className="fn-teaser-main">
                <h3 className="fn-teaser-title">{latestNote.title}</h3>
                <p className="fn-teaser-sub">{latestNote.subtitle}</p>
                <p className="fn-teaser-excerpt">{latestNote.excerpt}</p>
                <span className="fn-teaser-link">
                  Read the Field Note <ArrowRight size={15} />
                </span>
              </div>
            </Reveal>
            <div style={{ textAlign: "center", marginTop: "1.5rem" }}>
              <Link to="/field-notes" className="btn-ghost" data-testid="home-browse-field-notes">
                Browse all Field Notes
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* PARADIGM */}
      <section className="section bloom">
        <div className="container-page">
          <div className="feature-grid">
            <Reveal className="feature-copy">
              <Eyebrow>{paradigm.eyebrow}</Eyebrow>
              <h2 className="h-section">{paradigm.title}</h2>
              <p className="h-sub" style={{ marginTop: "0.75rem", marginBottom: "1.75rem" }}>
                {paradigm.sub}
              </p>
              <div className="prose-body">
                <Para items={paradigm.body} />
                <p className="pull-quote" style={{ margin: "1.5rem 0" }}>
                  {paradigm.quote}
                </p>
                <Para items={paradigm.body2} />
              </div>
            </Reveal>
            <Reveal delay={140} className="feature-media">
              <div className="img-frame img-veil img-difference" style={{ aspectRatio: "4 / 5" }}>
                <img src={IMAGES.stargazer} alt="A stargazer beneath the Milky Way" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* FOUNDATION */}
      <section className="section" style={{ background: "var(--paper-warm)" }}>
        <div className="container-page">
          <div className="feature-grid reverse">
            <Reveal className="feature-copy">
              <Eyebrow>{foundation.eyebrow}</Eyebrow>
              <h2 className="h-section">{foundation.title}</h2>
              <p className="h-sub" style={{ marginTop: "0.75rem", marginBottom: "1.75rem" }}>
                {foundation.sub}
              </p>
              <div className="prose-body">
                <Para items={foundation.body} />
              </div>
            </Reveal>
            <Reveal delay={140} className="feature-media">
              <div className="img-frame enso-frame" style={{ aspectRatio: "1 / 1" }}>
                <img src={IMAGES.enso} alt="A red ensō brushstroke circle with a solitary figure" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* BEYOND WORDS */}
      <section className="section bloom">
        <div className="container-page">
          <div className="feature-grid">
            <Reveal className="feature-copy">
              <Eyebrow>{beyond.eyebrow}</Eyebrow>
              <h2 className="h-section" style={{ fontStyle: "italic" }}>
                {beyond.title}
              </h2>
              <p className="h-sub" style={{ marginTop: "0.75rem", marginBottom: "1.75rem" }}>
                {beyond.sub}
              </p>
              <div className="prose-body">
                <Para items={beyond.body} />
              </div>
            </Reveal>
            <Reveal delay={140} className="feature-media">
              <div className="img-frame img-veil" style={{ aspectRatio: "4 / 5" }}>
                <img src={IMAGES.consciousness} alt="A figure resting within a field of light" loading="lazy" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* THE CONCEPT / PILLARS */}
      <section className="section" style={{ background: "var(--paper-warm)", position: "relative", overflow: "hidden" }}>
        <div className="constellation" />
        <div className="container-page">
          <Reveal>
            <SectionHeading
              eyebrow={concept.eyebrow}
              title={concept.title}
              sub={concept.sub}
              align="center"
            />
          </Reveal>
          <Reveal delay={100}>
            <p className="lead container-narrow" style={{ textAlign: "center", marginTop: "1.5rem" }}>
              {concept.intro}
            </p>
          </Reveal>
          <Reveal delay={140} style={{ display: "flex", justifyContent: "center", margin: "2.5rem 0 0.5rem" }}>
            <Ornament />
          </Reveal>

          <div className="pillars-grid" style={{ marginTop: "2.5rem" }}>
            {pillars.map((p, i) => {
              const Icon = pillarIcon[p.title] || Gem;
              return (
                <Reveal key={p.title} delay={i * 120} className="pillar soft-card">
                  <div
                    style={{
                      width: 78,
                      height: 78,
                      margin: "0 auto 1.25rem",
                      borderRadius: "50%",
                      display: "grid",
                      placeItems: "center",
                      background: "linear-gradient(160deg, #fbf3da, #f3e7bd)",
                      border: "1px solid var(--gold-strong)",
                    }}
                  >
                    <Icon size={34} color="var(--gold-ink)" strokeWidth={1.4} />
                  </div>
                  <h3>{p.title}</h3>
                  <div className="eyebrow" style={{ margin: "0.25rem 0 1rem", color: "var(--gold-strong)" }}>
                    {p.subtitle}
                  </div>
                  <p style={{ color: "var(--ink-soft)", fontSize: "0.98rem", lineHeight: 1.75 }}>{p.body}</p>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* CLOSING CTA */}
      <section className="section-tight bloom" style={{ paddingBottom: "clamp(4rem, 9vw, 7rem)", paddingTop: "clamp(4rem, 9vw, 7rem)" }}>
        <div className="container-page">
          <Reveal className="container-narrow" style={{ textAlign: "center" }}>
            <Ornament style={{ marginBottom: "1.25rem", justifyContent: "center", display: "inline-flex" }} />
            <h2 className="h-section">{closing.title}</h2>
            <p className="lead" style={{ marginTop: "1.25rem" }}>
              {closing.body}
            </p>
            <Link to="/contacts" className="btn-gold" style={{ marginTop: "2rem" }}>
              {closing.cta}
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
};

export default Home;
